import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import { lazy } from 'react';
import { PERMISSIONS } from '@/api/permissions';
import { paths } from '@/routes/paths';
import { RequirePermission } from '@/routes/guards';
import type { ModuleManifest } from './manifest';

const DeliveriesPage = lazy(() => import('@/features/deliveries/DeliveriesPage'));
const DeliveryDetailPage = lazy(() => import('@/features/deliveries/DeliveryDetailPage'));
const RequestDeliveryPage = lazy(() => import('@/features/deliveries/RequestDeliveryPage'));
const DeliveryPrintPage = lazy(() => import('@/features/deliveries/print/DeliveryPrintPage'));

/**
 * Deliveries, both audiences: a tenant reads what it sent and what is
 * addressed to it, platform staff read everything. The driver's own
 * transitions belong to the driver app and are never buttons here.
 */
export const logisticsModule: ModuleManifest = {
  key: 'logistics',
  titleKey: 'modules.logistics.title',
  descriptionKey: 'modules.logistics.description',
  icon: LocalShippingRoundedIcon,
  prefix: '/logistics',
  anyOf: [PERMISSIONS.DELIVERY_READ, PERMISSIONS.DELIVERY_MANAGE],
  sections: [
    {
      key: 'logistics',
      items: [
        {
          key: 'deliveries',
          labelKey: 'nav.deliveries',
          to: paths.deliveries,
          icon: <LocalShippingRoundedIcon />,
          anyOf: [PERMISSIONS.DELIVERY_READ, PERMISSIONS.DELIVERY_MANAGE],
        },
      ],
    },
  ],
  routes: [
    {
      element: (
        <RequirePermission
          permission={[PERMISSIONS.DELIVERY_READ, PERMISSIONS.DELIVERY_MANAGE]}
          mode="any"
        />
      ),
      children: [
        { path: paths.deliveries, element: <DeliveriesPage /> },
        { path: paths.delivery(), element: <DeliveryDetailPage /> },
      ],
    },
    {
      // A shipping-capable tenant requests; management creates on an
      // organization's behalf or as the platform.
      element: (
        <RequirePermission
          permission={[PERMISSIONS.DELIVERY_REQUEST, PERMISSIONS.DELIVERY_MANAGE]}
          mode="any"
        />
      ),
      children: [
        // The literal route has to precede the parameterised one.
        { path: paths.deliveryNew, element: <RequestDeliveryPage /> },
        { path: paths.deliveryEdit(), element: <RequestDeliveryPage /> },
      ],
    },
  ],
  // The order is paper, not a console page. Gated like the list — whoever may
  // read a delivery may print it.
  printRoutes: [
    {
      element: (
        <RequirePermission
          permission={[PERMISSIONS.DELIVERY_READ, PERMISSIONS.DELIVERY_MANAGE]}
          mode="any"
        />
      ),
      children: [{ path: paths.deliveryPrint(), element: <DeliveryPrintPage /> }],
    },
  ],
  landing: () => paths.deliveries,
};
